import React from 'react';
import { Search, PencilRuler, Rocket, Gauge, LucideIcon } from 'lucide-react';
import '../styles/Process.css';

interface Step {
  id: number;
  label: string;
  icon: LucideIcon;
  title: string;
  description: string;
  duration: string;
}

const STEPS: Step[] = [
  {
    id: 1,
    label: '01 · Discover',
    icon: Search,
    title: 'Map the operation',
    description:
      'We sit with your team, shadow the work, and map every SOP, tool, and hand-off. You leave this phase with a written automation plan—regardless of whether you work with us.',
    duration: 'Week 1',
  },
  {
    id: 2,
    label: '02 · Design',
    icon: PencilRuler,
    title: 'Architect the agent',
    description:
      'We pick models, integrations, guardrails, and observability. You review wireframes, example runs, and cost projections before a single line of production code is written.',
    duration: 'Week 2',
  },
  {
    id: 3,
    label: '03 · Deploy',
    icon: Rocket,
    title: 'Ship in weeks',
    description:
      'We build, test in your environment, and roll out in stages—silent pilot → human-shadow → autonomous. Your team trains alongside us so nothing is a black box.',
    duration: 'Weeks 3–6',
  },
  {
    id: 4,
    label: '04 · Optimise',
    icon: Gauge,
    title: 'Tune and expand',
    description:
      'Once live, we measure, tune, and plug leaks. As the agent earns trust, we expand scope and unlock the next process. You keep the code and the data.',
    duration: 'Ongoing',
  },
];

const Process: React.FC = () => {
  return (
    <section id="process" className="process section-padding">
      <div className="container">
        <div className="section-kicker reveal">
          <span className="eyebrow">How We Work</span>
          <h2>From workflow to working agent in under 30 days.</h2>
          <p>
            No marathon discovery decks. No vendor theatre. Four tight phases, clear
            deliverables, and a human you can text.
          </p>
        </div>

        <ol className="process-steps">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <li key={step.id} className="process-step reveal">
                <div className="step-meta">
                  <div className="step-icon">
                    <Icon size={18} />
                  </div>
                  <span className="step-label">{step.label}</span>
                </div>
                <div className="step-body">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <span className="step-duration">{step.duration}</span>
                </div>
                {i < STEPS.length - 1 && <div className="step-connector" aria-hidden="true" />}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default Process;
